import { Component, OnDestroy, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Events } from '../../helpers/events';

// ionic

// other libraries
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject, Subscription } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";

// components

// providers
import { StreamService } from "../../services/stream.service";
import { UserService } from "../../services/user.service";
import { EventService } from "../../services/event.service";
import { PlatformService } from "../../services/platform.service";
import { PaginatorService } from "../../services/paginator.service";

// models
import { Stream } from "../../models/stream";
import { User } from "../../models/user";
import { Event } from "../../models/event";

@Component({
    selector: 'stream-list',
    templateUrl: './stream-list.component.html',
    styleUrls: ['./stream-list.component.scss']
})
export class StreamListComponent implements OnInit, OnDestroy {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public dataLoading: boolean = false;

    /**
     * showing detail
     *
     * @type string
     */
    @Input() detail: boolean = false;

    /**
     * stream
     *
     * @type {Stream}
     */
    public items: Stream[] = [];

    itemsTotal: number = 0;
    itemsLimit: number = 5;
    itemsOffset: number = 0;

    pager: any;
    page: number = 1;

    @Input() event: Event;

    // handlers for events
    eventChangeHandler;
    refreshHandler;


    /**
     * constructor
     *
     * @param eventService
     * @param appEvents
     */
    constructor(
        public userService: UserService,
        public eventService: EventService,
        public translate: TranslateService,
        public appEvents: Events,
        public plt: PlatformService,
        public paginatorService: PaginatorService,
        public streamService: StreamService
    ) {
        this.userLang = this.translate.currentLang;
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        this.refreshHandler = () => {
            this.itemsOffset = 0;
            this.page = 1;
            this.refresh();
        }

        this.eventChangeHandler = () => {
            this.itemsOffset = 0;
            this.page = 1;
            this.refresh();
        }

        // attach hooks on event change and page navigation
        this.appEvents.subscribe('stream:refresh', this.refreshHandler);
        this.appEvents.subscribe('event:changed', this.eventChangeHandler);

        this.refresh();
    }

    /**
     * load more items in infinitive scroll
     *
     * @param event
     *
     * @return void
     */
    public loadData(event) {
        this.refresh(event, false);
    }

    /**
     * init list
     */
    public refresh($event?, reset = true) {

        this.userService.getCurrentUser().pipe(
            take(1),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {

                this.user = user;
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        if (event.id && this.event.is_homepage_content_stream) {

                            this.event = event;

                            if (!this.loading && !this.plt.disableBackgroundRefresh) {
                                // avoid parallel loading
                                this.loading = true;

                                if (reset) {
                                    this.itemsOffset = 0;
                                    this.page = 1;
                                }

                                if (this.event?.id) {
                                    let subscription = this.streamService.getListByEvent(
                                        this.event.id,
                                        this.itemsLimit,
                                        this.itemsOffset
                                    ).subscribe((response) => {
                                        if (reset) {
                                            this.items = [];
                                        }

                                        response['items'].forEach(item => {
                                            this.items.push(new Stream(item));
                                        });
                                        this.itemsTotal = response['total'];

                                        // update offset by real count of items
                                        this.itemsOffset = this.items.length;

                                        this.pager = this.paginatorService.getPager(this.itemsTotal, this.page, this.itemsLimit);

                                        this.loading = false;
                                        this.dataLoading = false;

                                        // hide loading
                                        if ($event) {
                                            $event.target.complete();
                                        }
                                    },
                                        (error) => {
                                            console.info(error);
                                            // hide loading
                                            if ($event) {
                                                $event.target.complete();
                                            } else {
                                                this.loading = false;
                                                this.dataLoading = false;
                                            }
                                        });

                                    this.plt.activeSubscriptions.push(subscription);
                                }
                            }
                        } else {
                            this.loading = false;
                            this.dataLoading = false;
                        }
                    });
            } else {
                this.loading = false;
                this.dataLoading = false;
            }
        });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }
        if (this.refreshHandler) {
            this.appEvents.unsubscribe('stream:refresh', this.refreshHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
